"use client";

import Link from "next/link";
import Image from "next/image";
import {
  ShoppingCartIcon,
  Search,
  Contact,
  Headset,
  GraduationCap,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { productsQueryKeys } from "@/lib/queries/products";
import { Field, FieldContent } from "./field";
import { SuggestionList } from "@/app/productos/SuggestionList";
import { useApi } from "@/lib/hooks/useApi";
import { useCartStore } from "@/lib/store/cartStore";
import { MegaMenu } from "./MegaMenu";
import UserSection from "./UserSection";
import { PricingPayload, Product } from "@/lib/models/product";
import type { SuggestItem } from "@/lib/models/search";
import { PricingResponse } from "@/lib/models/apiResponse";
import { CartLine } from "@/lib/models/cart";
import CurrencySelector from "./CurrencySelector";
import { useSession } from "next-auth/react";
import { useDictionaryStore } from "@/lib/store/dictionaryStore";
import { useCart } from "@/lib/hooks/useCart";
import { useLatestRequest } from "@/lib/hooks/useLatestRequest";
import { searchMinChars } from "@/lib/utils/constans";

export default function Header() {
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<Product[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const queryClient = useQueryClient();
  const { setExchangeRate } = useCartStore();
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const { getNextRequestId, isCurrent, invalidate } = useLatestRequest();
  const { data: session } = useSession();
  const { categoriesMenu } = useDictionaryStore();
  const { apiRequest } = useApi("");
  const roles = session?.user?.tokenDecode?.roles || [];
  const canUseCart =
    roles.includes("ROLE_CUSTOMER") && !roles.includes("ROLE_SUPPLIER");
  const { cart } = useCart();

  // ─── Session Monitor (Diagnóstico Profundo) ──────────────────────────────
  const sessionRef = useRef(session);
  const jwtExpRef = useRef<number | null>(null);

  // Sincronizar refs en cada render
  useEffect(() => {
    sessionRef.current = session;
    const jwtExp = session?.user?.jwtExp;
    if (jwtExp) jwtExpRef.current = jwtExp;
  }, [session]);

  // Intervalo maestro: solo en desarrollo, lee siempre de las refs
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;

    const logTick = () => {
      const s = sessionRef.current;
      const rawExp = jwtExpRef.current;
      const timeStr = new Date().toLocaleTimeString();

      if (!s) {
        console.log(
          `%c[SESSION] ❌ DESCONECTADO | ${timeStr}`,
          "color: #94a3b8; font-weight: bold;",
        );
        return;
      }

      if (!rawExp) {
        console.log(
          `%c[SESSION] ⚠️ CONECTADO PERO SIN EXP | ${timeStr}`,
          "color: #f59e0b; font-weight: bold;",
        );
        return;
      }

      // Auto-detección: si es > 10^12, probablemente sean milisegundos
      const isMs = rawExp > 1000000000000;
      const expInMs = isMs ? rawExp : rawExp * 1000;
      const expInSec = isMs ? Math.floor(rawExp / 1000) : rawExp;

      const nowMs = Date.now();
      const nowSec = Math.floor(nowMs / 1000);

      const timeLeft = expInSec - nowSec;
      const expDate = new Date(expInMs);

      // Formateador de duración amigable
      const formatDuration = (s: number) => {
        if (s < 0) return "EXPIRADO";
        const d = Math.floor(s / 86400);
        const h = Math.floor((s % 86400) / 3600);
        const m = Math.floor((s % 3600) / 60);
        const secs = s % 60;
        const parts = [];
        if (d > 0) parts.push(`${d}d`);
        if (h > 0) parts.push(`${h}h`);
        if (m > 0) parts.push(`${m}m`);
        parts.push(`${secs}s`);
        return parts.join(" ");
      };

      const color =
        timeLeft < 0
          ? "background: #fee2e2; color: #b91c1c"
          : timeLeft < 300
            ? "color: #f59e0b"
            : "color: #10b981";

      const rtShort = "N/A";

      console.log(
        `%c[SESSION] ⏱️ ${formatDuration(timeLeft)} | Exp: ${expDate.toLocaleTimeString()} | Ahora: ${new Date().toLocaleTimeString()} | RAW: ${rawExp} | RT: ${rtShort}`,
        `${color}; font-weight: bold;`,
      );

      // Si el usuario quiere ver la data completa para depurar
      if (timeLeft % 10 === 0) {
        console.log("[SESSION DATA]", {
          timeLeft,
          exp: expDate.toLocaleString(),
          now: new Date().toLocaleString(),
          jwtExp: rawExp,
        });
      }

      if (timeLeft < -60) {
        console.error(
          "[SESSION] 🚨 SESIÓN EXPIRADA hace más de 1 minuto. El refresh falló o no se ejecutó.",
        );
      }
    };

    const interval = setInterval(logTick, 1000);
    return () => clearInterval(interval);
  }, []); // Se monta una sola vez y corre por siempre

  useEffect(() => {
    if (searchQuery.length >= searchMinChars) {
      setLoading(true);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(async () => {
        const requestId = getNextRequestId();
        try {
          const data = await apiRequest<{
            data: { suggestions: SuggestItem[] };
          }>("search/products/suggest", {
            method: "post",
            data: {
              query: searchQuery,
              size: 20,
            },
            loadingMessage: "Buscando productos...",
          });

          if (!isCurrent(requestId)) return;

          const suggestionsData = data?.data?.suggestions || [];

          if (suggestionsData.length > 0) {
            const pricingItems = suggestionsData.map((suggestion) => ({
              sku: suggestion.sku,
            }));

            try {
              const dataSend: PricingPayload = { items: pricingItems };

              const pricingResponse = await apiRequest<PricingResponse>("", {
                method: "post",
                data: dataSend,
                url: `${process.env.NEXT_PUBLIC_API_URL}/pricing/calculate-batch`,
                loadingMessage: "Cargando productos...",
              });

              if (!isCurrent(requestId)) return;

              const pricingData = pricingResponse?.data?.precios || [];

              const validExchangeRate =
                pricingData.find(
                  (p: { tasaCambio?: number | null }) =>
                    typeof p.tasaCambio === "number",
                )?.tasaCambio ?? useCartStore.getState().exchangeRate;
              if (validExchangeRate) {
                setExchangeRate(validExchangeRate);
              }

              const suggestionsWithPrice = suggestionsData.map(
                (suggestion) => {
                  const priceData = pricingData.find(
                    (p) => p.sku === suggestion.sku,
                  );
                  return {
                    ...suggestion,
                    price: priceData,
                  };
                },
              );

              setSuggestions(suggestionsWithPrice);
            } catch {
              if (isCurrent(requestId)) {
                setSuggestions(suggestionsData);
              }
            }
          } else {
            setSuggestions([]);
          }

          if (isCurrent(requestId)) {
            setShowSuggestions(true);
          }
        } catch {
          if (isCurrent(requestId)) {
            setSuggestions([]);
          }
        } finally {
          if (isCurrent(requestId)) {
            setLoading(false);
          }
        }
      }, 300);
    } else {
      invalidate();
      setSuggestions([]);
      setShowSuggestions(false);
      setLoading(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      invalidate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const searchContainerRef = useRef<HTMLDivElement | null>(null);
  const mobileSearchContainerRef = useRef<HTMLDivElement | null>(null);

  // Cerrar sugerencias al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideDesktop = searchContainerRef.current?.contains(target);
      const isInsideMobile = mobileSearchContainerRef.current?.contains(target);
      if (!isInsideDesktop && !isInsideMobile) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearch = (e?: React.FormEvent | React.KeyboardEvent) => {
    if (e) {
      e.preventDefault();
    }
    const query = searchQuery.trim();
    if (!query) return;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    setSearchQuery("");
    setSuggestions([]);
    setShowSuggestions(false);

    if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    // Invalidar caché de productos para que siempre recargue la búsqueda
    queryClient.invalidateQueries({ queryKey: productsQueryKeys.all });

    const targetUrl = `/productos?query=${encodeURIComponent(query)}`;
    if (typeof window !== "undefined" && window.location.pathname === "/productos") {
      const currentSearch = window.location.search;
      const currentQuery = new URLSearchParams(currentSearch).get("query");
      if (currentQuery === query && currentSearch === `?query=${encodeURIComponent(query)}`) {
        router.refresh();
        return;
      }
    }
    router.push(targetUrl);
  };

  const buttonStyles =
    "border !text-secondary flex px-4 py-2.5 gap-1.5 justify-center items-center text-sm font-medium cursor-pointer rounded-lg !text-md !font-semibold transition-colors duration-200 hover:bg-secondary text-secondary hover:!text-white";
  return (
    <>
      <header className=" top-0 z-50 bg-white ">
        <div className="hidden md:flex items-center justify-between gap-2 lg:gap-4 p-1 px-3 lg:px-4 xl:px-6 w-full">
          <div className="flex items-center gap-2 lg:gap-4 min-w-0 flex-1">
            <Link href="/" className="flex-shrink-0">
              <Image
                src="/Logoazulhorizontal.png"
                alt="Adises"
                priority
                width={170}
                height={45}
                style={{ height: "auto" }}
                className="hidden md:block w-[120px] lg:w-[150px] xl:w-[170px]"
              />
            </Link>

            <form
              onSubmit={handleSearch}
              className="flex-1 min-w-0 max-w-[420px] md:max-w-[480px] lg:max-w-[560px] xl:max-w-[700px] 2xl:max-w-[850px]"
            >
              <Field orientation="horizontal" className="flex-1 m-0">
                <FieldContent className="gap-0">
                  <div ref={searchContainerRef} className="relative flex-1">
                    <input
                      type="text"
                      aria-label="Buscar productos en la tienda"
                      placeholder="Buscar productos..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() =>
                        searchQuery.length >= 3 && setShowSuggestions(true)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSearch(e);
                        }
                      }}
                      className="border border-gray-300 w-full px-3 lg:px-4 py-2 pl-9 lg:pl-10 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                    <Search
                      size={18}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                    />
                    <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />

                    {/* Dropdown de sugerencias debajo del input */}
                    {showSuggestions && (
                      <div className="absolute top-full left-0 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 overflow-y-auto max-h-[400px]">
                        <div className="px-4 py-2">
                          {!loading && suggestions.length > 0 && (
                            <SuggestionList
                              productsSugestion={suggestions}
                              closeSuggestion={() => {
                                setShowSuggestions(false);
                                setSearchQuery("");
                              }}
                            />
                          )}
                          {!loading && suggestions.length === 0 && (
                            <div className="p-2 text-center text-gray-500 text-xs">
                              No se encontraron sugerencias
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </FieldContent>
              </Field>
            </form>
          </div>

          <div className="flex items-center justify-end gap-1.5 lg:gap-2 pr-1 lg:pr-3 flex-shrink-0">
            <CurrencySelector />

            {canUseCart && (
              <Link
                href="/carrito"
                aria-label="Ver carrito de compras"
                className="min-h-[40px] max-h-[40px] max-w-[40px] min-w-[40px] flex items-center justify-center group-hover:gap-2  hover:min-w-[90px] border border-gray-300  text-secondary rounded-full hover:rounded-lg transition-all duration-300 relative group"
              >
                <ShoppingCartIcon size={18} className="flex-shrink-0" />
                {/* Badge contador */}
                {cart?.lines && cart.lines.length > 0 && (
                  <span className="absolute -bottom-1 -right-1 text-primary bg-white text-[8px] rounded-full px-1.5 py-0.5 min-w-[15px] text-center border-1 border-secondary shadow">
                    {cart.lines
                      .map((line: CartLine) => line.quantity ?? 0)
                      .reduce((a: number, b: number) => a + b, 0)}
                  </span>
                )}
                <span className="w-0 group-hover:w-auto overflow-hidden whitespace-nowrap text-sm font-medium transition-all duration-300">
                  {" "}
                  Carrito
                </span>
              </Link>
            )}
            <UserSection />
          </div>
        </div>
        <div className="ml-2 flex flex-wrap gap-1 lg:gap-1.5 xl:gap-2 items-center text-white justify-start hidden md:flex mb-1 pt-1">
          <MegaMenu categories={categoriesMenu} buttonStyles={buttonStyles} />
          <Link className={buttonStyles} href="/contacto">
            <Contact size={15} />
            <span className="hidden lg:inline">Contacto</span>
          </Link>
          <Link className={buttonStyles} href="/soporte">
            <Headset size={15} />
            <span className="hidden lg:inline">Soporte técnico</span>
          </Link>
          <a
            className={buttonStyles}
            href="https://cursos.adises.com.mx/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <GraduationCap size={16} />
            <span className="hidden lg:inline">Cursos</span>
          </a>
          {/* <button className={buttonStyles}>
            <Newspaper size={15} />
            <span>Noticias</span>
          </button> */}
        </div>
        <div className=" p-1 xs:p-1.5 block border-b mb-1 border-gray-200 md:hidden grid grid-cols-6 ">
          <div className="flex items-center p-1 col-span-3">
            <MegaMenu categories={categoriesMenu} buttonStyles={buttonStyles} />

            <Link href="/" className="">
              <Image
                src="/logo_blanco_responsive.png"
                alt="Adises"
                width={50}
                height={0}
                className="w-[42px] xs:w-[50px] h-auto"
              />
            </Link>
          </div>

          <div className="col-span-3 flex items-center justify-end gap-1 xs:gap-2 pr-1 xs:pr-3">
            {/* Contenedor principal de moneda + tasa (ahora en vertical) */}
            <div className="flex flex-col items-center">
              <CurrencySelector />
            </div>
            {canUseCart && (
              <Link
                href="/carrito"
                aria-label="Ver carrito de compras"
                className="min-h-[36px] xs:min-h-[40px] max-h-[36px] xs:max-h-[40px] max-w-[36px] xs:max-w-[40px] min-w-[36px] xs:min-w-[40px] flex items-center justify-center group-hover:gap-2  hover:min-w-[90px] border border-gray-300  text-secondary rounded-full hover:rounded-lg transition-all duration-300 relative group"
              >
                <ShoppingCartIcon size={18} className="flex-shrink-0" />
                {/* Badge contador */}
                {cart?.lines && cart.lines.length > 0 && (
                  <span className="absolute -bottom-1 -right-1 text-primary bg-white text-[8px] rounded-full px-1.5 py-0.5 min-w-[15px] text-center border-1 border-secondary shadow">
                    {cart.lines
                      .map((line: CartLine) => line.quantity ?? 0)
                      .reduce((a: number, b: number) => a + b, 0)}
                  </span>
                )}
                <span className="w-0 group-hover:w-auto overflow-hidden whitespace-nowrap text-sm font-medium transition-all duration-300">
                  {" "}
                  Carrito
                </span>
              </Link>
            )}
            <UserSection />
          </div>
          <div className="col-span-6 flex items-center mt-2 mb-2">
            <form onSubmit={handleSearch} className="flex gap-2 w-full">
              <Field orientation="horizontal" className="flex-1 m-0">
                <FieldContent className="gap-0">
                  <div ref={mobileSearchContainerRef} className="relative flex-1">
                    <input
                      type="text"
                      aria-label="Buscar productos en la tienda"
                      placeholder="Buscar productos..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() =>
                        searchQuery.length >= 3 && setShowSuggestions(true)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSearch(e);
                        }
                      }}
                      className="border border-gray-300 w-full px-4 py-2 pl-10 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                    <Search
                      size={18}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                    />
                    <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />

                    {/* Dropdown de sugerencias debajo del input (móvil) */}
                    {showSuggestions && (
                      <div className="absolute top-full left-0 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 overflow-y-auto max-h-[400px]">
                        <div className="px-4 py-2">
                          {!loading && suggestions.length > 0 && (
                            <SuggestionList
                              productsSugestion={suggestions}
                              closeSuggestion={() => {
                                setShowSuggestions(false);
                                setSearchQuery("");
                              }}
                            />
                          )}
                          {!loading && suggestions.length === 0 && (
                            <div className="p-2 text-center text-gray-500 text-xs">
                              No se encontraron sugerencias
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </FieldContent>
              </Field>
            </form>
          </div>
          <div className="col-span-6 flex items-center gap-1.5 pb-1 overflow-x-auto no-scrollbar">
            <Link
              className="flex-1 min-w-max border border-gray-200 !text-secondary flex px-2.5 py-1.5 gap-1.5 justify-center items-center rounded-lg text-xs font-semibold transition-colors duration-200 hover:bg-secondary hover:!text-white bg-white shadow-xs"
              href="/contacto"
            >
              <Contact size={14} className="text-primary shrink-0" />
              <span>Contacto</span>
            </Link>
            <Link
              className="flex-1 min-w-max border border-gray-200 !text-secondary flex px-2.5 py-1.5 gap-1.5 justify-center items-center rounded-lg text-xs font-semibold transition-colors duration-200 hover:bg-secondary hover:!text-white bg-white shadow-xs"
              href="/soporte"
            >
              <Headset size={14} className="text-primary shrink-0" />
              <span>Soporte técnico</span>
            </Link>
            <a
              className="flex-1 min-w-max border border-gray-200 !text-secondary flex px-2.5 py-1.5 gap-1.5 justify-center items-center rounded-lg text-xs font-semibold transition-colors duration-200 hover:bg-secondary hover:!text-white bg-white shadow-xs"
              href="https://cursos.adises.com.mx/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <GraduationCap size={15} className="text-primary shrink-0" />
              <span>Cursos</span>
            </a>
          </div>
        </div>
      </header>

      {/* Overlay oscuro cuando hay sugerencias */}
      {showSuggestions && (
        <div
          className="fixed inset-0 bg-black/50 z-30"
          onClick={() => setShowSuggestions(false)}
        />
      )}
    </>
  );
}
